import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  Activity, AlertTriangle, FileSignature, Receipt, ArrowUpRight, Sparkles, ShieldCheck,
  Loader2, Copy, RefreshCw, X, Brain, Zap, CheckCircle2, Clock, CalendarDays, CircleDot,
  CheckCheck, Circle, ChevronRight, FolderPlus, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import ActivityFeed from "@/components/app/ActivityFeed";
import StatTile from "@/components/app/StatTile";

// ── Arago neon status palette ─────────────────────────────────────────────────
const HEALTH_BAR = {
  green: "#00F5D4",   // nominal teal
  amber: "#F59E0B",   // early-warning amber
  red:   "#FF007A",   // critical neon pink
};
const HEALTH_PILL = {
  green: "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/25 shadow-glow-teal",
  amber: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/25 shadow-glow-amber",
  red:   "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/25 shadow-glow-pink",
};
const HEALTH_RING_HOVER = {
  green: "hover:border-[#00F5D4]/30",
  amber: "hover:border-[#F59E0B]/30",
  red:   "hover:border-[#FF007A]/30",
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [aging, setAging] = useState(null);
  const [pendingCompliance, setPendingCompliance] = useState([]);
  const [pmoAlerts, setPmoAlerts] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem("cw_onboarding_dismissed") === "1"
  );
  const [forecastProgress, setForecastProgress] = useState(null);

  const generateBrief = async () => {
    setBriefBusy(true);
    try {
      const { data } = await api.post("/ai/steering-brief", { days: 7 });
      setBrief(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate brief");
    } finally {
      setBriefBusy(false);
    }
  };

  const copyBrief = async () => {
    if (!brief?.brief) return;
    try {
      await navigator.clipboard.writeText(brief.brief);
      toast.success("Brief copied to clipboard");
    } catch {
      toast.error("Copy failed — your browser blocked clipboard access");
    }
  };

  useEffect(() => {
    (async () => {
      const [o, a, c, p, mt, ob, fp] = await Promise.all([
        api.get("/portfolio/overview"),
        api.get("/invoices/aging"),
        api.get("/compliance/documents?status=pending_review"),
        api.get("/pmo-alerts"),
        api.get("/tasks/my"),
        api.get("/onboarding/status").catch(() => ({ data: null })),
        api.get("/dashboard/forecast-progress").catch(() => ({ data: null })),
      ]);
      setOverview(o.data);
      setAging(a.data);
      setPendingCompliance(c.data);
      setPmoAlerts(p.data || []);
      const myData = mt.data || {};
      const todayAndOverdue = (myData.tasks || []).filter((t) => {
        if (t.status === "done") return false;
        return t.due_bucket === "today" || t.due_bucket === "overdue";
      });
      setTodayTasks(todayAndOverdue.slice(0, 8));
      if (ob.data && !ob.data.all_done) setOnboarding(ob.data);
      if (fp.data && fp.data.total_projects > 0 && fp.data.needs_attention_count > 0) setForecastProgress(fp.data);
    })();
  }, []);

  const dismissAlert = async (alertId) => {
    const prev = pmoAlerts;
    setPmoAlerts(prev.filter((al) => al.alert_id !== alertId));
    try {
      await api.post(`/pmo-alerts/${alertId}/dismiss`);
      toast.success("Alert dismissed");
    } catch (e) {
      setPmoAlerts(prev);
      toast.error(e.response?.data?.detail || "Failed to dismiss alert");
    }
  };

  if (!overview) {
    return (
      <div className="min-h-screen bg-[#030712] grid place-items-center" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#00F5D4]/30 border-t-[#00F5D4] rounded-full animate-spin mx-auto mb-4" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-mono">
            Initialising command centre…
          </div>
        </div>
      </div>
    );
  }

  const overdueCount = aging?.invoice_breakdown?.filter((i) => i.days_overdue > 0).length || 0;
  const outstandingPool = aging?.total_outstanding || overview.total_outstanding;
  const liveProjects = overview.projects.filter((p) => p.status !== "completed");

  return (
    <div className="min-h-screen bg-[#030712] text-[#F9FAFB] font-sans antialiased" data-testid="executive-dashboard">

      {/* Ambient mesh gradients — non-interactive light sources */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#00F5D4]/5 rounded-full blur-[160px]" />
        <div className="absolute top-24 right-1/4 w-[420px] h-[420px] bg-[#FF007A]/4 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 p-6 lg:p-8">

        {/* 1. Header ─────────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-8 pb-6 border-b border-[#1F2937]">
          <div>
            <p className="text-xs font-medium tracking-widest text-[#00F5D4] uppercase mb-1.5">
              Dashboard
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-white" data-testid="dashboard-title">
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <p className="text-[#7d8590] text-sm mt-1">
              {user?.tenant_name || "Your workspace"} · Here's what needs your attention today.
            </p>
          </div>
          <div
            className="flex items-center gap-2 bg-[#161b22] border border-[#30363d] px-3.5 py-2 rounded-lg text-xs text-[#00F5D4]"
            data-testid="system-status"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F5D4] animate-glow-teal" />
            <span className="font-medium">Systems operational</span>
          </div>
        </div>

        {/* ONBOARDING CHECKLIST ───────────────────────────────────────────────── */}
        {onboarding && !onboardingDismissed && (
          <div className="mb-8 border border-[#002FA7]/40 bg-[#002FA7]/5 rounded-xl overflow-hidden" data-testid="onboarding-panel">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#002FA7]/20">
              <div className="flex items-center gap-2.5">
                <Zap className="w-4 h-4 text-[#002FA7]" style={{ color: "#4F8EF7" }} />
                <span className="text-sm font-semibold text-white">Get started with CombinedWork</span>
                <span className="text-[10px] font-mono text-slate-500 ml-1">
                  {onboarding.completed}/{onboarding.total} complete
                </span>
              </div>
              <button
                onClick={() => { setOnboardingDismissed(true); localStorage.setItem("cw_onboarding_dismissed", "1"); }}
                className="text-slate-600 hover:text-slate-400 transition"
                data-testid="onboarding-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="h-2 bg-[#1F2937]">
              <div
                className="h-full bg-[#00F5D4] transition-all duration-500"
                style={{ width: `${(onboarding.completed / onboarding.total) * 100}%` }}
              />
            </div>
            <div className="divide-y divide-[#1F2937]/50">
              {onboarding.steps.map((step, idx) => {
                const firstIncomplete = onboarding.steps.findIndex((s) => !s.done);
                const isHighlighted = !step.done && idx === firstIncomplete;
                return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 px-5 py-3 transition-colors ${
                    step.done ? "opacity-50" : "hover:bg-[#002FA7]/8 cursor-pointer"
                  } ${isHighlighted ? "border-l-4 border-[#00F5D4]" : ""}`}
                  onClick={() => !step.done && step.link && navigate(step.link)}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  {step.done
                    ? <CheckCheck className="w-4 h-4 text-[#4F8EF7] flex-shrink-0" />
                    : <Circle className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${step.done ? "line-through text-slate-500" : isHighlighted ? "text-white font-bold" : "text-white"}`}>
                      {step.label}
                    </div>
                    {!step.done && (
                      <div className="text-xs text-slate-500 mt-0.5">{step.description}</div>
                    )}
                  </div>
                  {!step.done && step.link && (
                    <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FORECAST PROGRESS LINE ─────────────────────────────────────────────── */}
        {forecastProgress && (
          <div className="mb-8 border border-[#1F2937] bg-[#0D1117] rounded-lg overflow-hidden" data-testid="forecast-progress-panel">
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[#1F2937]">
              <Activity className="w-4 h-4 text-[#00F5D4]" />
              <span className="text-sm font-semibold text-white" data-testid="forecast-progress-line">
                {forecastProgress.forecasting} of {forecastProgress.total_projects} projects forecasting
              </span>
              <span className="text-xs text-[#F59E0B] ml-1">
                {forecastProgress.needs_attention_count} need{forecastProgress.needs_attention_count === 1 ? "s" : ""} attention
              </span>
            </div>
            <div className="divide-y divide-[#1F2937]/50">
              {forecastProgress.needs_attention.slice(0, 6).map((p) => (
                <div
                  key={p.project_id}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  onClick={() => navigate(`/burndown?project=${p.project_id}`)}
                  data-testid={`forecast-progress-row-${p.project_id}`}
                >
                  <span className="text-sm text-white flex-1 min-w-0 truncate">{p.name}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-[#F59E0B]/30 text-[#F59E0B] bg-[#F59E0B]/10 shrink-0">
                    {p.label || p.state}
                  </span>
                  {p.fix_action && <span className="text-xs text-slate-500 shrink-0 hidden sm:block">{p.fix_action}</span>}
                  <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TODAY PANEL ────────────────────────────────────────────────────────── */}
        {(todayTasks.length > 0 || overdueCount > 0) && (
          <div className="mb-8 border border-[#1F2937] bg-[#0D1117] rounded-lg overflow-hidden" data-testid="today-panel">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[#1F2937] bg-[#030712]">
              <CalendarDays className="w-4 h-4 text-[#00F5D4]" />
              <span className="text-sm font-semibold text-white">Today</span>
              <span className="text-xs text-[#484f58] ml-0.5">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
            </div>
            <div className="divide-y divide-[#1F2937]">
              {todayTasks.map((t) => (
                <Link
                  key={t.task_id}
                  to={`/projects/${t.project_id}?tab=board`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#111827] transition group"
                  data-testid={`today-task-${t.task_id}`}
                >
                  {t.due_bucket === "overdue"
                    ? <AlertTriangle className="w-3.5 h-3.5 text-[#FF007A] flex-shrink-0" />
                    : <CircleDot className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
                  }
                  <span className="text-sm text-slate-300 flex-1 truncate group-hover:text-white">{t.title}</span>
                  {t.project?.name && (
                    <span className="text-[10px] text-slate-600 font-mono truncate max-w-[120px]">{t.project.name}</span>
                  )}
                  {t.due_bucket === "overdue" && (
                    <span className="text-[10px] font-mono text-[#FF007A] bg-[#FF007A]/10 px-1.5 py-0.5 rounded flex-shrink-0">OVERDUE</span>
                  )}
                  {t.due_bucket === "today" && (
                    <span className="text-[10px] font-mono text-[#F59E0B] bg-[#F59E0B]/10 px-1.5 py-0.5 rounded flex-shrink-0">DUE TODAY</span>
                  )}
                </Link>
              ))}
              {overdueCount > 0 && (
                <Link
                  to="/invoices"
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#111827] transition group"
                  data-testid="today-overdue-invoices"
                >
                  <Receipt className="w-3.5 h-3.5 text-[#FF007A] flex-shrink-0" />
                  <span className="text-sm text-slate-300 flex-1 group-hover:text-white">
                    {overdueCount} overdue invoice{overdueCount !== 1 ? "s" : ""}
                  </span>
                  <span className="text-[10px] font-mono text-[#FF007A] bg-[#FF007A]/10 px-1.5 py-0.5 rounded flex-shrink-0">ACTION NEEDED</span>
                </Link>
              )}
            </div>
            {todayTasks.length === 0 && overdueCount === 0 && (
              <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-[#00F5D4]" />
                All clear — nothing due today.
              </div>
            )}
          </div>
        )}

        {/* 2. Macro metric tier ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <MetricCard
            testid="metric-projects"
            icon={Activity}
            eyebrow="Active SOW Pipelines"
            value={liveProjects.length}
            unit="projects"
            subline={`${overview.avg_completion}% avg completion`}
            accent="teal"
          />
          <MetricCard
            testid="metric-billings"
            icon={Receipt}
            eyebrow="Outstanding Billings"
            value={`£${(outstandingPool || 0).toLocaleString()}`}
            subline={overdueCount > 0
              ? `${overdueCount} invoice${overdueCount === 1 ? "" : "s"} past due`
              : "All invoices current"}
            accent={overdueCount > 0 ? "amber" : "teal"}
          />
          <MetricCard
            testid="metric-compliance"
            icon={FileSignature}
            eyebrow="Compliance Queue"
            value={pendingCompliance.length}
            unit={pendingCompliance.length === 1 ? "item" : "items"}
            subline={pendingCompliance.length > 0
              ? "Awaiting sign-off"
              : "Inbox clear"}
            accent={pendingCompliance.length > 0 ? "amber" : "teal"}
          />
        </div>

        {/* Quick Actions ─────────────────────────────────────────────────────── */}
        {overview.total_projects === 0 ? (
          <div className="mb-8 bg-[#111827] border border-[#00F5D4]/20 rounded-xl p-10 text-center" data-testid="empty-state-welcome">
            <Zap className="w-8 h-8 text-[#00F5D4] mx-auto mb-3" />
            <h2 className="text-lg font-bold text-white">Welcome to CombinedWork</h2>
            <p className="text-sm text-slate-400 mt-1 mb-5">Create your first project to get started.</p>
            <Link
              to="/projects/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4] text-sm font-semibold rounded transition-colors"
            >
              <FolderPlus className="w-4 h-4" /> Create your first project
            </Link>
          </div>
        ) : (
          <div className="mb-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quick-actions">
            {[
              { label: "New Project", to: "/workspace?new=1", Icon: FolderPlus },
              { label: "Log time", to: "/timesheets", Icon: Clock },
              { label: "Add task", to: "/my-tasks", Icon: ClipboardList },
              { label: "New invoice", to: "/invoices?new=1", Icon: Receipt },
            ].map(({ label, to, Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-2.5 px-4 py-3 bg-[#111827] border border-[#1F2937] hover:border-[#00F5D4]/30 rounded-lg text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Icon className="w-4 h-4 text-[#00F5D4] flex-shrink-0" />
                {label}
              </Link>
            ))}
          </div>
        )}

        {/* 3. Two-column main stage ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Active SOW Pipelines */}
          <div className="lg:col-span-2 space-y-6">
            <Panel
              title="Live Enterprise Portfolio"
              rightLink={{ to: "/portfolio", label: "Full portfolio" }}
              testid="panel-projects"
            >
              {liveProjects.length === 0 ? (
                <EmptyState message="No active projects. Start a new pipeline from Workspace." />
              ) : (
                <div className="divide-y divide-[#1F2937]">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 pb-2 px-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7d8590]">Project</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7d8590]">Progress</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7d8590] text-right">Health</span>
                  </div>
                  {liveProjects.slice(0, 7).map((p) => (
                    <Link
                      key={p.project_id}
                      to={`/workspace/${p.project_id}`}
                      className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3.5 px-1 transition-colors duration-150 ${HEALTH_RING_HOVER[p.health] || "hover:border-slate-600"} hover:bg-[#111827]/40 rounded group`}
                      data-testid={`pipeline-${p.project_id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: HEALTH_BAR[p.health] || "#475569" }}
                          />
                          <h4 className="text-sm font-medium text-white truncate group-hover:text-[#00F5D4] transition-colors">{p.name}</h4>
                        </div>
                        <p className="text-xs text-[#7d8590] mt-0.5 pl-3.5 flex flex-wrap gap-x-2">
                          <span>{p.methodology}</span>
                          <span>{p.tasks_done}/{p.tasks_total} tasks</span>
                          {p.open_raid > 0 && (
                            <span className="text-[#FF007A]">{p.open_raid} RAID{p.critical_raid > 0 ? ` (${p.critical_raid} critical)` : ""}</span>
                          )}
                          {p.overdue_tasks > 0 && (
                            <span className="text-[#F59E0B]">{p.overdue_tasks} overdue</span>
                          )}
                        </p>
                        <div className="mt-2 h-0.5 bg-[#1F2937] rounded overflow-hidden ml-3.5 mr-4">
                          <div
                            className="h-full rounded transition-all duration-500"
                            style={{
                              width: `${p.completion_pct}%`,
                              background: HEALTH_BAR[p.health] || "#475569",
                              boxShadow: `0 0 6px ${HEALTH_BAR[p.health] || "#475569"}60`,
                            }}
                          />
                        </div>
                      </div>
                      <span className="font-mono text-sm font-bold" style={{ color: HEALTH_BAR[p.health] || "#94a3b8" }}>
                        {p.completion_pct}%
                      </span>
                      <StatusPill health={p.health} />
                    </Link>
                  ))}
                </div>
              )}
            </Panel>

            {/* Activity Feed */}
            <Panel title="Recent Activity" testid="panel-activity-feed">
              <ActivityFeed limit={10} />
            </Panel>

            {/* Attention list */}
            {overview.attention?.length > 0 && (
              <Panel title="Needs Attention" testid="panel-attention" tone="critical">
                <div className="space-y-2">
                  {overview.attention.slice(0, 4).map((p) => (
                    <Link
                      key={p.project_id}
                      to={`/workspace/${p.project_id}`}
                      className="flex items-center gap-3 p-3 bg-red-500 border border-red-600 rounded-lg hover:bg-red-600 transition group"
                      data-testid={`attention-${p.project_id}`}
                    >
                      <AlertTriangle className="w-4 h-4 text-gray-900 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                        <div className="text-[10px] text-gray-900/80 font-mono">
                          {p.critical_raid > 0 && <span className="mr-2 font-semibold">{p.critical_raid} critical RAID</span>}
                          {p.overdue_tasks > 0 && <span className="mr-2 font-semibold">{p.overdue_tasks} overdue tasks</span>}
                        </div>
                      </div>
                      <ArrowUpRight className="w-3.5 h-3.5 text-gray-900 transition-colors" />
                    </Link>
                  ))}
                </div>
              </Panel>
            )}
          </div>

          {/* Right column — action feed */}
          <div className="space-y-6">

            {/* Compliance sign-off queue */}
            <Panel
              title="Action Required"
              rightLink={pendingCompliance.length > 0 ? { to: "/compliance", label: "Compliance" } : null}
              testid="panel-actions"
            >
              {pendingCompliance.length === 0 ? (
                <EmptyState message="Inbox clear. Nothing awaiting your signature." icon={ShieldCheck} />
              ) : (
                <div className="space-y-3">
                  {pendingCompliance.slice(0, 5).map((doc) => (
                    <div key={doc.document_id} className="p-4 bg-[#030712] border border-[#F59E0B]/20 rounded-lg" data-testid={`action-${doc.document_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                        <p className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-[0.2em] font-mono">Pending Sign-Off</p>
                      </div>
                      <h4 className="text-sm font-medium text-white mt-1.5 truncate">{doc.title}</h4>
                      <p className="text-[10px] text-slate-500 mt-1 font-mono">
                        {doc.project_id ? "Project-scoped" : "Tenant-wide"}
                        <span className="mx-1.5">·</span>
                        {doc.created_by_name || "team member"}
                      </p>
                      <Link
                        to="/compliance"
                        className="block text-center w-full mt-3 py-2 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-semibold font-mono rounded transition-colors"
                        data-testid={`review-${doc.document_id}`}
                      >
                        Review &amp; Execute Sign-Off
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* PMO Alerts */}
            <Panel
              title="PMO Alerts"
              testid="panel-pmo-alerts"
              tone={pmoAlerts.length > 0 ? "critical" : undefined}
            >
              {pmoAlerts.length === 0 ? (
                <EmptyState message="No predictive margin alerts. Forecasts run nightly." icon={Brain} />
              ) : (
                <div className="space-y-3" data-testid="pmo-alerts-list">
                  {pmoAlerts.slice(0, 6).map((al) => {
                    const isHigh = al.severity === "High";
                    const border = isHigh ? "border-[#FF007A]/25" : "border-[#F59E0B]/25";
                    const sevCls = isHigh ? "text-[#FF007A]" : "text-[#F59E0B]";
                    const project = overview.projects.find((p) => p.project_id === al.project_id);
                    return (
                      <div
                        key={al.alert_id}
                        className={`p-4 bg-[#030712] border ${border} rounded-lg`}
                        data-testid={`pmo-alert-${al.alert_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Brain className={`w-3.5 h-3.5 ${sevCls} shrink-0`} />
                            <p className={`text-[10px] font-bold ${sevCls} uppercase tracking-[0.2em] font-mono`}>
                              {al.severity} · {al.alert_type}
                            </p>
                          </div>
                          <button
                            onClick={() => dismissAlert(al.alert_id)}
                            className="text-slate-600 hover:text-white transition-colors p-0.5"
                            title="Dismiss"
                            data-testid={`dismiss-pmo-alert-${al.alert_id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <h4 className="text-sm font-medium text-white mt-1.5 truncate">
                          {project?.name || "Project"}
                        </h4>
                        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{al.message}</p>
                        <div className="mt-2 font-mono text-[10px] text-slate-600">
                          Forecast margin <span className={sevCls}>{al.predicted_final_margin}%</span>
                          <span className="mx-1.5">·</span>
                          £{(al.predicted_final_cost || 0).toLocaleString()}
                        </div>
                        <Link
                          to={`/workspace/${al.project_id}`}
                          className="block text-center w-full mt-3 py-1.5 bg-[#111827] hover:bg-[#1F2937] border border-[#1F2937] text-slate-300 hover:text-white text-xs font-mono rounded transition-colors"
                          data-testid={`open-pmo-alert-${al.alert_id}`}
                        >
                          Open project →
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            {/* AI Co-pilot — steering brief */}
            <div
              className="bg-[#111827] border border-violet-200 rounded-xl p-5 shadow-glow-teal"
              data-testid="assistant-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-100 border border-violet-200 grid place-items-center shrink-0">
                  <Zap className={`w-4 h-4 text-violet-600 ${briefBusy ? "animate-pulse" : ""}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-violet-600 tracking-wide">AI Co-pilot</div>
                  <h4 className="text-sm font-semibold text-white mt-0.5">Steering brief</h4>
                  <p className="text-xs text-[#7d8590] mt-1">Distils RAID, time burn, and chat into an executive status update.</p>
                </div>
              </div>

              {!brief && (
                <button
                  onClick={generateBrief}
                  disabled={briefBusy}
                  className="w-full mt-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed border border-violet-600 text-white text-xs font-mono font-semibold rounded transition-colors flex items-center justify-center gap-2"
                  data-testid="generate-brief-btn"
                >
                  {briefBusy
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Composing…</>
                    : <><Sparkles className="w-3.5 h-3.5" /> Generate brief (7 days)</>
                  }
                </button>
              )}

              {brief && (
                <div className="mt-4 space-y-3" data-testid="brief-output">
                  <div
                    className="bg-[#030712] border border-[#1F2937] rounded p-3 text-[12px] leading-relaxed text-slate-200 whitespace-pre-wrap"
                    data-testid="brief-text"
                  >
                    {brief.brief}
                  </div>
                  <div className="text-[10px] text-slate-600 font-mono flex flex-wrap gap-x-3 gap-y-1">
                    <span>{brief.stats.projects} project{brief.stats.projects === 1 ? "" : "s"}</span>
                    <span>· {brief.stats.total_hours}h burned</span>
                    <span>· £{(brief.stats.total_cash || 0).toLocaleString()}</span>
                    <span>· {brief.stats.open_raid} RAID ({brief.stats.critical_raid} crit)</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copyBrief}
                      className="flex-1 py-1.5 border border-violet-300 hover:border-violet-500 text-violet-600 text-xs font-mono rounded transition-colors flex items-center justify-center gap-1.5"
                      data-testid="copy-brief-btn"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button
                      onClick={generateBrief}
                      disabled={briefBusy}
                      className="px-3 py-1.5 border border-[#1F2937] hover:border-slate-600 text-slate-400 hover:text-white text-xs rounded transition-colors"
                      title="Regenerate"
                      data-testid="regen-brief-btn"
                    >
                      {briefBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => setBrief(null)}
                      className="px-3 py-1.5 border border-[#1F2937] hover:border-slate-600 text-slate-400 hover:text-white text-xs rounded transition-colors"
                      title="Dismiss"
                      data-testid="dismiss-brief-btn"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

// MetricCard defers to the shared StatTile so this page's numbers match
// Reporting/Invoices/My Tasks/Daily Standup/Burndown instead of keeping its
// own font/weight/colour recipe. "teal"/"amber" map onto StatTile's
// semantic success/warning variants.
const ACCENT_TO_COLOUR = { teal: "success", amber: "warning", pink: "danger" };

const MetricCard = ({ eyebrow, value, unit, subline, accent = "teal", icon, testid }) => (
  <StatTile
    testid={testid}
    icon={icon}
    label={eyebrow}
    value={value}
    unit={unit}
    sub={subline}
    colour={ACCENT_TO_COLOUR[accent] || "success"}
  />
);

const Panel = ({ title, children, rightLink, testid, tone }) => {
  const borderCls = tone === "critical"
    ? "border-[#FF007A]/15"
    : "border-[#1F2937]";
  return (
    <div className={`bg-[#111827] border ${borderCls} rounded-xl p-5`} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7d8590]">{title}</h3>
        {rightLink && (
          <Link
            to={rightLink.to}
            className="text-xs text-[#00F5D4] hover:text-white flex items-center gap-1 transition-colors"
          >
            {rightLink.label} <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
};

const StatusPill = ({ health }) => {
  const label = health === "red" ? "Critical" : health === "amber" ? "At risk" : "On track";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${HEALTH_PILL[health] || HEALTH_PILL.green}`}>
      {label}
    </span>
  );
};

const EmptyState = ({ message, icon: Icon = ShieldCheck }) => (
  <div className="text-center py-8">
    <Icon className="w-5 h-5 text-[#484f58] mx-auto mb-2" strokeWidth={1.5} />
    <p className="text-xs text-[#7d8590]">{message}</p>
  </div>
);

export default Dashboard;
